/**
 * useInstallmentSession — Hook do checkout público para garantir
 * (criar ou reaproveitar) a sessão pública de uma parcela irmã da
 * cobrança atualmente aberta em /c/:slug.
 *
 * Chama a edge function `public-checkout` na action
 * `ensure_installment_session`, que valida vínculo da parcela com a
 * cobrança-mãe (slug atual) e gera/reaproveita o slug daquela parcela.
 *
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useInstallmentSession');

interface EnsureResult {
  slug: string;
  expires_at?: string;
  reused?: boolean;
}

export function useInstallmentSession() {
  const [pendingParcelaId, setPendingParcelaId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Garante slug da parcela irmã. Retorna `null` em falha (já tratada
   * via toast/logger pelo consumidor superior se necessário).
   */
  const ensure = useCallback(
    async (parentSlug: string, parcelaId: string): Promise<string | null> => {
      // Defesa em profundidade — formatos.
      if (!/^[a-f0-9]{32}$/.test(parentSlug)) {
        logger.warn('parent slug inválido');
        return null;
      }
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parcelaId)) {
        logger.warn('parcela id inválido');
        return null;
      }
      setPendingParcelaId(parcelaId);
      setError(null);
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke(
          'public-checkout',
          {
            body: {
              action: 'ensure_installment_session',
              slug: parentSlug,
              parcela_id: parcelaId,
            },
          },
        );
        if (invokeErr) {
          logger.warn('ensure_installment_session falhou', { msg: invokeErr.message });
          setError('Não foi possível abrir esta parcela. Tente novamente.');
          return null;
        }
        const result = data as EnsureResult | { error?: string } | null;
        if (!result || (result as { error?: string }).error || !('slug' in result)) {
          setError((result as { error?: string })?.error ?? 'Resposta inválida');
          return null;
        }
        return result.slug;
      } catch (err) {
        logger.error('ensure_installment_session exceção', { err: String(err) });
        setError('Erro de conexão. Tente novamente.');
        return null;
      } finally {
        setPendingParcelaId(null);
      }
    },
    [],
  );

  return { ensure, pendingParcelaId, error };
}
